import gql from 'graphql-tag';

import GraphQLClient from '../../../adapter/GraphQLClient';
import Language from '../../../entity/Language';
import { GetOfferByIdQuery } from '../../../graphql/generated/graphql_offer';
import { ArrayElement } from '../../../utility/graphql';
import { DigNonNullable } from '../../../utilityTypes/DigNonNullable';
import { ApplyForOfferMutation, GetOffersQuery } from './../../../graphql/generated/graphql_offer';
import { assignnmentFragment } from './AssignmentWebApi';

export type Offer = NonNullable<NonNullable<ArrayElement<DigNonNullable<GetOffersQuery, ['offers', 'edges']>>>['node']>;

const singleOfferFragment = gql`
  fragment SingleOffer on Offer {
    id
    name
    jobId
    type
    rewardCurrency
    rewardAmount
    rewardQuota
    availableFrom
    availableTo
    expectedDue
    workerStatus
    workerPermissions
    assignmentCount
    candidateCount
    matchingFormat
    canCreativeVisibilityOnProfile
    expressDelivery
    organization {
      id
      name
    }
    creativeFormats {
      id
      network
      creativeType
      resizeBaseId
      placement {
        key
        name
      }
      attribute {
        aspectRatio {
          width
          height
        }
        size {
          width
          height
        }
        playbackTime {
          seconds
        }
        fastStart
      }
      selectedPricingMerchandiseOptions {
        id
        name
        displayToOptimizer
        helpUrlForOptimizer
      }
    }
  }
`;

class OfferWebApi {
  private offerGraphqlClient: GraphQLClient;

  constructor({ offerGraphqlClient }: { offerGraphqlClient: GraphQLClient }) {
    this.offerGraphqlClient = offerGraphqlClient;
  }

  public getOffers = async ({
    language,
    filter,
    itemCount,
    startCursor,
  }: {
    language: Language;
    filter: {
      includeClosed: boolean;
    };
    itemCount: number;
    startCursor: string | null;
  }): Promise<{ items: Offer[]; endCursor: string | null }> => {
    const result: GetOffersQuery = (await this.offerGraphqlClient.query(
      gql`
        query getOffers($language: ID!, $filter: OfferFilter, $first: Int, $after: String) {
          offers(language: $language, filter: $filter, first: $first, after: $after) {
            edges {
              # Offer
              node {
                id
                name
                jobId
                type
                rewardCurrency
                rewardAmount
                rewardQuota
                availableFrom
                availableTo
                expectedDue
                workerStatus
                workerPermissions
                assignmentCount
                candidateCount
                matchingFormat
                expressDelivery
                organization {
                  id
                  name
                }
                creativeFormats {
                  id
                  network
                  creativeType
                  resizeBaseId
                  placement {
                    key
                    name
                  }
                  attribute {
                    aspectRatio {
                      width
                      height
                    }
                    size {
                      width
                      height
                    }
                    playbackTime {
                      seconds
                    }
                    fastStart
                  }
                }
              }
            }
            pageInfo {
              endCursor
              hasNextPage
            }
          }
        }
      `,
      {
        language,
        filter,
        first: itemCount,
        after: startCursor,
      },
    )) as GetOffersQuery;

    const { edges, pageInfo } = result.offers;
    const res = edges?.map((edge) => edge?.node)?.filter((n): n is Offer => !!n);
    if (edges && res && edges.length === res.length) {
      return {
        items: res,
        endCursor: pageInfo.hasNextPage && pageInfo.endCursor ? pageInfo.endCursor : null,
      };
    } else {
      throw new Error('result.offers.edges must be an array.');
    }
  };

  public getById = async (id: string): Promise<GetOfferByIdQuery['offer']> => {
    const result: GetOfferByIdQuery = (await this.offerGraphqlClient.query(
      gql`
        ${singleOfferFragment}
        query getOfferById($id: ID!) {
          offer(id: $id) {
            ...SingleOffer
          }
        }
      `,
      {
        id,
      },
    )) as GetOfferByIdQuery;
    if (result.offer) {
      return result.offer;
    } else {
      throw new Error('result.offer must be an object.');
    }
  };

  public applyForOffer = async (
    id: string,
  ): Promise<DigNonNullable<ApplyForOfferMutation, ['applyForOffer', 'assignment']>> => {
    const result: ApplyForOfferMutation = await this.offerGraphqlClient.query(
      gql`
        ${assignnmentFragment}
        mutation applyForOffer($id: ID!) {
          applyForOffer(input: { offerId: $id }) {
            assignment {
              ...SingleAssignment
            }
          }
        }
      `,
      {
        id,
      },
    );
    if (result.applyForOffer?.assignment) {
      return result.applyForOffer.assignment;
    } else {
      throw new Error('result.applyForOffer must be an object.');
    }
  };
}

export default OfferWebApi;
